import styled from '@emotion/styled'
import { ApiMutualFundAssetRead, ApiSegregatedPortfolioAssetRead } from 'api/rtk-api'
import { useState } from 'react'

import { unpascal } from '../../../../modules/tradebuilder-v2/util'
import { LinkButton, Space } from '../../atoms'
import Icon from '../../atoms/Icon'
import IconButton from '../../molecules/iconButton'
import LinkIcon from '../../molecules/linkIcon'
import { AssetInformation } from '../asset-information'
import { ProgressContainer } from '../progress-container'
import { Column, ResultTable } from '../result-table'

export type AssetLookupProps = {
  assets?: (ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]
  onClickClose?: () => void
  onSelectAsset?: (asset: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => void
  title?: string
}

const StyledLink = styled.div`
  * {
    text-align: left;
  }
`

const StyledActions = styled.div`
  border-top: 1px solid var(--White90);
`

const StyledResultTableWrapper = styled.div`
  height: 574px;
  overflow: scroll;
`

const StyledAssetLookupWrapper = styled.div`
  height: 610px;
`

const StyledAssetInformationWrapper = styled.div`
  height: 500px;
  overflow: hidden;
`

export function AssetLookup({
  assets,
  onClickClose,
  onSelectAsset,
  title = 'Find an asset'
}: AssetLookupProps) {
  const [isInspecting, setIsInspecting] = useState(false)
  const [assetBeingInspected, setAssetBeingInspected] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()

  const columns: Column<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>[] = [
    {
      name: 'name',
      renderHeader: data => <>{data.length} Assets available</>,
      render: (item: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => (
        <StyledLink>
          <Space mb={1}>
            <LinkButton
              underline={false}
              onClick={() => {
                setIsInspecting(true)
                setAssetBeingInspected(item)
              }}
            >
              {item.name}
            </LinkButton>
          </Space>
          {item.isinCode}
        </StyledLink>
      )
    },
    {
      name: 'type',
      title: 'Type',
      render: (item: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => (
        <>{unpascal(item.type)}</>
      )
    },
    {
      name: 'protectionClassification',
      title: 'Protection class',
      render: (item: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => (
        <>{unpascal(item.protectionClassification)}</>
      )
    },
    {
      name: 'taxClassification',
      title: 'Tax classification'
    },
    {
      name: 'assetClass',
      title: 'Asset class'
    }
  ]

  return (
    <ProgressContainer
      currentStep={isInspecting ? 2 : 1}
      numSteps={2}
      title={title}
      onClickClose={onClickClose}
    >
      <StyledAssetLookupWrapper>
        {!isInspecting && (
          <Space mt={6} mx={4}>
            <StyledResultTableWrapper>
              <ResultTable columns={columns} data={assets} />
            </StyledResultTableWrapper>
          </Space>
        )}
        {isInspecting && (
          <>
            <StyledAssetInformationWrapper>
              <AssetInformation asset={assetBeingInspected} />
            </StyledAssetInformationWrapper>
            <StyledActions>
              <Space isflex alignItems="center" justifyContent="space-between" p={6}>
                <LinkIcon
                  disabled={false}
                  icon={<Icon name="chevronLeft" size="small" />}
                  iconPosition="left"
                  text="Back to search"
                  onClick={() => setIsInspecting(false)}
                />
                <IconButton
                  data-testid="add-asset-button"
                  disabled={false}
                  icon={<Icon name="chevronRight" size="small" />}
                  iconPosition="right"
                  operationType="progress"
                  text="Add Asset"
                  onClick={() => onSelectAsset(assetBeingInspected)}
                />
              </Space>
            </StyledActions>
          </>
        )}
      </StyledAssetLookupWrapper>
    </ProgressContainer>
  )
}
